(function (global) {
  'use strict';
  // ============================================================
  // PLUMME 3 — Prosjektkobling paa kostnader
  //
  // Et kjop, en innboksrad og en ansatts utleggslinje kan hoere til et prosjekt.
  // Feltene er de samme alle tre steder, og normaliseres her sa de ikke driver
  // fra hverandre. Dette er BEVISST ikke en dimensjonsmodell paa
  // hovedbokslinjer: koblingen ligger paa bilagshodet og brukes til
  // fakturagrunnlag, ikke til kontering.
  //
  // `projectInvoiceStatus` foelger samme moenster som timeforingens
  // `invoiceStatus` ('open' | 'invoiced'), slik at timer og utlegg kan frigis og
  // fakturaeres med den samme logikken.
  // ============================================================

  const OPEN = 'open';
  const INVOICED = 'invoiced';

  const text = (value) => String(value == null ? '' : value).trim();

  // Bare `false` slaar av fakturering. Et bilag uten uttrykt valg er
  // fakturerbart — det er derfor det ble satt paa prosjektet.
  const normalizeBillable = (value) => value !== false && value !== 'false' && value !== 0;

  // Returnerer et objekt uten tomme felter, klart til aa spres inn i en post.
  // Uten prosjekt er resultatet tomt: en kostnad uten prosjekt skal ikke baere
  // med seg doede faktureringsfelter.
  const normalizeProjectCostFields = (source = {}) => {
    const projectId = text(source.projectId);
    if (!projectId) return {};
    const status = text(source.projectInvoiceStatus) === INVOICED ? INVOICED : OPEN;
    const fields = { projectId, projectBillable: normalizeBillable(source.projectBillable), projectInvoiceStatus: status };
    if (status === INVOICED) {
      const invoiceId = text(source.projectInvoiceId);
      const invoicedAt = text(source.projectInvoicedAt);
      if (invoiceId) fields.projectInvoiceId = invoiceId;
      if (invoicedAt) fields.projectInvoicedAt = invoicedAt;
    }
    return fields;
  };

  // Klar for fakturagrunnlaget: paa prosjekt, fakturerbar og ikke alt fakturert.
  const isProjectCostInvoiceable = (record = {}) =>
    Boolean(text(record.projectId)) && normalizeBillable(record.projectBillable) && text(record.projectInvoiceStatus) !== INVOICED;

  const markProjectCostInvoiced = (record = {}, { invoiceId, invoicedAt } = {}) => ({
    ...record,
    projectInvoiceStatus: INVOICED,
    projectInvoiceId: text(invoiceId) || undefined,
    projectInvoicedAt: text(invoicedAt) || new Date().toISOString(),
  });

  const releaseProjectCost = (record = {}) => {
    const next = { ...record, projectInvoiceStatus: OPEN };
    delete next.projectInvoiceId;
    delete next.projectInvoicedAt;
    return next;
  };

  // ---- Fakturagrunnlag ----------------------------------------------------
  // Kostnadene paa et prosjekt kommer fra to steder, og begge maa med:
  //   1. Kjop (`expenses`) — bilag bokfort via innboksen eller kjopsskjemaet.
  //   2. Ansattes private utlegg (`employeePortalTravels`) — de bokfores IKKE
  //      som kjop, fordi refusjonen allerede gaar gjennom lonn. Uten dem ville
  //      halvparten av prosjektets kostnader manglet i grunnlaget.
  // Firmakort-linjer holdes utenfor: de er dokumentasjon, og kostnaden kommer
  // inn som et vanlig kjop.
  const list = (value) => (Array.isArray(value) ? value : []);
  const amount = (value) => { const number = Number(value); return Number.isFinite(number) ? number : 0; };

  const PURCHASE = 'purchase';
  const EMPLOYEE_EXPENSE = 'employee-expense';
  const PENDING = 'pending';

  // Kunden spor «hva gjelder dette?», og da ma bilaget vaere ett klikk unna —
  // ikke noe man leter etter i kjopene. Kostnadsraden baerer derfor med seg
  // filreferansene sine. Feltene er de samme som `voucherAttachments` leser i
  // backend-mappers, men lest her sa modulen forblir ren og testbar.
  const attachmentsOf = (entry = {}, fallbackName) => {
    const rows = list(entry.attachments).map((attachment) => ({
      id: text(attachment.id) || text(attachment.attachmentId) || text(attachment.storagePath) || text(attachment.name),
      name: text(attachment.name) || text(entry.attachmentName) || fallbackName,
      mimeType: text(attachment.mimeType) || text(attachment.type) || text(entry.attachmentMimeType),
      storagePath: text(attachment.storagePath),
      downloadUrl: text(attachment.downloadUrl) || text(attachment.file),
    }));
    const fallbackUrl = text(entry.file) || text(entry.downloadUrl) || text(entry.receiptFile) || text(entry.attachmentDownloadUrl);
    if (fallbackUrl || text(entry.attachmentStoragePath)) {
      rows.push({
        id: text(entry.attachmentId) || text(entry.id),
        name: text(entry.attachmentName) || fallbackName,
        mimeType: text(entry.attachmentMimeType),
        storagePath: text(entry.attachmentStoragePath),
        downloadUrl: fallbackUrl,
      });
    }
    // En rad uten fil er ingen lenke. Da er det aerligere a si at bilaget
    // mangler enn a tilby en knapp som apner ingenting.
    return Array.from(new Map(rows
      .filter((row) => row.downloadUrl || row.storagePath)
      .map((row) => [row.downloadUrl || row.storagePath, row])).values());
  };

  const costStatus = (record) => (text(record.projectInvoiceStatus) === INVOICED ? INVOICED : OPEN);

  const purchaseCost = (expense, line = null) => {
    const source = line || expense;
    const attachments = attachmentsOf(expense, text(expense.supplierName) || 'Bilag');
    return {
      kind: PURCHASE,
      key: line ? `purchase:${text(expense.id)}:${text(line.id)}` : `purchase:${text(expense.id)}`,
      id: text(line?.id) || text(expense.id),
      expenseId: text(expense.id),
      projectId: text(source.projectId),
      date: text(expense.date),
      description: text(expense.description) || text(expense.supplierName) || 'Kjøp',
      partyName: text(expense.supplierName),
      voucherNumber: text(expense.voucherNumber) || text(expense.supplierInvoiceNumber),
      amountNet: amount(line?.amountNet ?? expense.amountNet),
      billable: normalizeBillable(source.projectBillable),
      status: costStatus(source),
      invoiceId: text(source.projectInvoiceId),
      attachments,
      hasAttachment: attachments.length > 0,
    };
  };

  const employeeCost = (travel, line, pending) => {
    const attachments = attachmentsOf(line, text(line.description) || 'Bilag');
    return {
      kind: EMPLOYEE_EXPENSE,
      key: `employee-expense:${text(travel.id)}:${text(line.id)}`,
      id: text(line.id),
      travelId: text(travel.id),
      employeeName: text(travel.employeeName),
      projectId: text(line.projectId),
      date: text(line.date) || text(travel.dateFrom),
      description: text(line.description) || text(line.category) || 'Utlegg',
      partyName: text(travel.employeeName),
      amountNet: amount(line.amountNet) || amount(line.amount),
      billable: normalizeBillable(line.projectBillable),
      status: pending ? PENDING : costStatus(line),
      invoiceId: text(line.projectInvoiceId),
      attachments,
      hasAttachment: attachments.length > 0,
    };
  };

  /**
   * `scope: 'all'` er oversikten, ikke grunnlaget: den tar ogsa med det som
   * allerede er fakturert, det som er merket ikke-fakturerbart, og utlegg som
   * venter pa godkjenning — nettopp fordi sporsmalet «hva har vi brukt pa dette
   * prosjektet?» ikke stopper ved det som kan faktureres. Standardvalget er
   * uendret, sa faktureringen plukker fortsatt bare det den skal.
   */
  const collectProjectCosts = (state = {}, { projectId, scope } = {}) => {
    const wanted = text(projectId);
    const all = scope === 'all';
    const costs = [];
    list(state.expenses).forEach((expense) => {
      const lineCosts = list(expense.purchaseLines).filter((line) => text(line.projectId));
      if (lineCosts.length) {
        lineCosts.forEach((line) => {
          if (wanted && text(line.projectId) !== wanted) return;
          if (text(expense.status) === 'draft') return;
          if (!all && !isProjectCostInvoiceable(line)) return;
          costs.push(purchaseCost(expense, line));
        });
        return;
      }
      if (!text(expense.projectId)) return;
      if (wanted && text(expense.projectId) !== wanted) return;
      if (text(expense.status) === 'draft') return;
      if (!all && !isProjectCostInvoiceable(expense)) return;
      costs.push(purchaseCost(expense));
    });
    list(state.employeePortalTravels).forEach((travel) => {
      // Bare godkjente utlegg i grunnlaget. Et innsendt utlegg kan fortsatt bli
      // avvist, og skal ikke ligge i et fakturagrunnlag for arbeidsgiveren har
      // sagt ja. I oversikten vises det, tydelig merket som ventende.
      const status = text(travel.status);
      const pending = status === 'submitted';
      if (status !== 'approved' && !(all && pending)) return;
      list(travel.expenseLines).forEach((line) => {
        if (!text(line.projectId)) return;
        if (wanted && text(line.projectId) !== wanted) return;
        if (text(line.paymentMethod) !== 'private') return;
        if (!all && !isProjectCostInvoiceable(line)) return;
        costs.push(employeeCost(travel, line, pending));
      });
    });
    return costs.sort((a, b) => a.date.localeCompare(b.date));
  };

  const totalCostAmount = (costs) => Math.round(list(costs).reduce((sum, cost) => sum + amount(cost.amountNet), 0) * 100) / 100;

  /**
   * Viderefakturering av en kostnad er ET VANLIG SALG: nettobelopet fra kjopet
   * blir en salgslinje, og fakturamotoren avgjor mva og salgskonto slik den
   * gjor for alt annet salg. Vi innfoerer ingen egen mva-regel her.
   *
   * «Utlegg i kundens navn» (gjennomstromning uten fradrag og uten utgaaende
   * mva) er en ANNEN behandling, og stottes bevisst ikke: den krever at bilaget
   * er utstedt til kunden, noe vi ikke kan lese ut av et bokfort kjop.
   */
  const buildProjectCostInvoiceLines = (costs, { salesAccountCode = '3020' } = {}) => list(costs)
    .filter((cost) => amount(cost.amountNet) > 0)
    .map((cost) => ({
      costKey: cost.key,
      description: cost.description,
      note: cost.kind === EMPLOYEE_EXPENSE && cost.employeeName ? `Utlegg ${cost.employeeName}` : undefined,
      unit: 'stk',
      quantity: 1,
      unitPrice: Math.round(amount(cost.amountNet) * 100) / 100,
      amount: Math.round(amount(cost.amountNet) * 100) / 100,
      salesAccountCode,
      type: 'Tjeneste',
    }));

  // Merking og frigjoring skjer paa den samme staten begge veier, slik at et
  // slettet utkast alltid gir kostnadene tilbake til grunnlaget.
  const applyProjectCostInvoiceState = (state = {}, costs, { invoiceId, invoicedAt } = {}) => {
    const keys = new Set(list(costs).map((cost) => cost.key));
    if (!keys.size) return state;
    const stamp = { invoiceId, invoicedAt: invoicedAt || new Date().toISOString() };
    return {
      ...state,
      expenses: list(state.expenses).map((expense) => {
        const next = keys.has(`purchase:${text(expense.id)}`) ? markProjectCostInvoiced(expense, stamp) : expense;
        return Array.isArray(expense.purchaseLines) ? { ...next, purchaseLines: expense.purchaseLines.map((line) => (keys.has(`purchase:${text(expense.id)}:${text(line.id)}`) ? markProjectCostInvoiced(line, stamp) : line)) } : next;
      }),
      employeePortalTravels: list(state.employeePortalTravels).map((travel) => ({
        ...travel,
        expenseLines: list(travel.expenseLines).map((line) => (keys.has(`employee-expense:${text(travel.id)}:${text(line.id)}`)
          ? markProjectCostInvoiced(line, stamp) : line)),
      })),
    };
  };

  const releaseProjectCostsFromInvoice = (state = {}, invoiceId) => {
    const target = text(invoiceId);
    if (!target) return { state, released: 0 };
    let released = 0;
    const release = (record) => {
      if (text(record.projectInvoiceId) !== target) return record;
      released += 1;
      return releaseProjectCost(record);
    };
    const next = {
      ...state,
      expenses: list(state.expenses).map((expense) => {
        const next = release(expense);
        return Array.isArray(expense.purchaseLines) ? { ...next, purchaseLines: expense.purchaseLines.map(release) } : next;
      }),
      employeePortalTravels: list(state.employeePortalTravels).map((travel) => ({
        ...travel,
        expenseLines: list(travel.expenseLines).map(release),
      })),
    };
    return { state: released ? next : state, released };
  };

  const api = {
    PROJECT_COST_OPEN: OPEN,
    PROJECT_COST_INVOICED: INVOICED,
    PROJECT_COST_PENDING: PENDING,
    PROJECT_COST_PURCHASE: PURCHASE,
    PROJECT_COST_EMPLOYEE_EXPENSE: EMPLOYEE_EXPENSE,
    normalizeProjectCostFields,
    isProjectCostInvoiceable,
    markProjectCostInvoiced,
    releaseProjectCost,
    collectProjectCosts,
    totalCostAmount,
    buildProjectCostInvoiceLines,
    applyProjectCostInvoiceState,
    releaseProjectCostsFromInvoice,
  };

  global.PlummeProjectCostLink = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
}(typeof globalThis !== 'undefined' ? globalThis : window));
